import { Button, Divider, Form, Input, Modal, Switch, Select, Tabs} from 'antd';
import React, { useEffect, useState } from 'react';
import * as Icon from "react-feather";
import { useDispatch } from 'react-redux';
import serviceLinkService from '../../services/service-link-service';
import appLoaderAction from '../../redux/action/app-loader-action';
import notifyUtitls from '../../utils/notify-utils';
import EditableTagGroup from '../../components/input/editable-tag-group';
import { VISIBILITY_DEFAULT, SERVICE_LINK_ICON_OPTIONS } from '../../constants/app-constants';
import VisibilityFormItem from '../../components/input/visibility-form-item';
import ContentSelect from '../../components/input/content-select';
import { LINK_TYPE } from '../../constants/app-constants';
import { formText, langOfTab } from '../../constants/form-i18n';


const { TabPane } = Tabs;

const defaultValue = {
    isOpenNewTab: false,
    keywordList: [],
};

const TAB_TH = '1';
const TAB_EN = '2';

/**
 * NMU-424: which language tab owns each field, so a failed validation can open
 * the tab holding the first error instead of leaving the user on a clean-looking
 * form. Fields absent from this map are the common ones below the tabs.
 */
const FIELD_TAB = {
    iconNameTH: TAB_TH,
    iconNameEN: TAB_EN,
    title: TAB_TH,
    descriptionTH: TAB_TH,
    badgeTH: TAB_TH,
    titleEN: TAB_EN,
    descriptionEN: TAB_EN,
    badgeEN: TAB_EN,
};

const AddEditServiceLinkDialog = ({ isShow, onCancel, onSaveComplete, editData }) => {
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const [keywordList, setKeywordList] = useState(defaultValue.keywordList);
    const [activeTab, setActiveTab] = useState(TAB_TH);
    // NMU-444: the tab this dialog already tracks also drives its language.
    const lang = langOfTab(activeTab);
    const t = formText(lang);

    useEffect(() => {
        if (!isShow) {
            setKeywordList(defaultValue.keywordList);
            setActiveTab(TAB_TH);
        }
    }, [isShow]);

    useEffect(() => {
        if (isShow && editData) {
            form.setFieldsValue({
                iconNameTH: editData.iconNameTH,
                iconNameEN: editData.iconNameEN,
                title: editData.title,
                titleEN: editData.titleEN,
                descriptionTH: editData.descriptionTH,
                descriptionEN: editData.descriptionEN,
                badgeTH: editData.badgeTH,
                badgeEN: editData.badgeEN,
                icon: editData.icon ? editData.icon : undefined,
                // NMU-470: records written before this ticket have no linkType and
                // are all URLs — default them so the form opens on the right branch
                // instead of an empty selector with a filled-in link underneath.
                linkType: editData.linkType || (editData.templateId ? LINK_TYPE.CONTENT : LINK_TYPE.URL),
                templateId: editData.templateId,
                url: editData.url,
                status: editData.status,
                totalView: editData.totalView,
                enableNewTab: editData.enableNewTab,
                visibility: editData.visibility ? editData.visibility : VISIBILITY_DEFAULT
            });

            setKeywordList(editData.keywordList ? editData.keywordList : defaultValue.keywordList);
         
        }
    }, [isShow, editData]);

    const onSave = async () => {
        try {
            const values = await form.validateFields();
            try {

                dispatch(appLoaderAction.enable());
                
                if (!editData) {
                    let bean = {
                        iconNameTH: values.iconNameTH,
                        iconNameEN: values.iconNameEN,
                        title: values.title,
                        titleEN: values.titleEN,
                        descriptionTH: values.descriptionTH,
                        descriptionEN: values.descriptionEN,
                        badgeTH: values.badgeTH,
                        badgeEN: values.badgeEN,
                        icon: values.icon,
                        keywordList: keywordList,
                        linkType: values.linkType,
                        // Only one of the two is meaningful; clear the other so a
                        // switched type cannot leave a stale target behind (the
                        // NMU-456 "เมนู02" failure).
                        templateId: values.linkType === LINK_TYPE.CONTENT ? values.templateId : null,
                        url: values.linkType === LINK_TYPE.URL ? values.url : null,
                        enableNewTab: values.enableNewTab,
                        status: values.status,
                        visibility: values.visibility,
                        totalView: 0
                    };

                    await serviceLinkService.add({bean: bean});
                } else {
                    let bean = JSON.parse(JSON.stringify(editData));
                    bean.iconNameTH = values.iconNameTH;
                    bean.iconNameEN = values.iconNameEN;
                    bean.title = values.title;
                    bean.titleEN = values.titleEN;
                    bean.descriptionTH = values.descriptionTH;
                    bean.descriptionEN = values.descriptionEN;
                    bean.badgeTH = values.badgeTH;
                    bean.badgeEN = values.badgeEN;
                    // NMU-431: fields removed from the form — keep whatever the record
                    // already has instead of clearing it on every save.
                    bean.icon = values.icon;
                    // NMU-447: the display order field is gone from the form; leave any
                    // value an existing record still carries rather than wiping it.
                    bean.keywordList = keywordList;
                    bean.linkType = values.linkType;
                    bean.templateId = values.linkType === LINK_TYPE.CONTENT ? values.templateId : null;
                    bean.url = values.linkType === LINK_TYPE.URL ? values.url : null;
                    bean.enableNewTab = values.enableNewTab;
                    bean.status = values.status;
                    bean.visibility = values.visibility;
                    bean.totalView = editData.totalView;
                    await serviceLinkService.edit({bean: bean});
                }

                dispatch(appLoaderAction.disale());
                notifyUtitls.success(t('successTitle'), t('saveSuccess'));
                onSaveComplete();
            } catch (err) {
                notifyUtitls.error(err);
                dispatch(appLoaderAction.disale());
            }
        } catch (errorInfo) {
            // Open the tab holding the first invalid field - otherwise a required
            // field on the tab the user is not looking at reads as "nothing wrong".
            const firstField = errorInfo?.errorFields?.[0]?.name?.[0];
            const tab = FIELD_TAB[firstField];
            if (tab) {
                setActiveTab(tab);
            }
            notifyUtitls.warning(t('warningTitle'), t('incompleteForm'));
        }
    };

    return (
        <div className="container h-100">
            <div className="title-page">
                <Modal
                    title={
                        t(editData ? 'editServiceLink' : 'addServiceLink')
                    }
                    centered
                    width={750}
                    visible={isShow}
                    onCancel={onCancel}
                    destroyOnClose
                    footer={[
                        <Button key="submit" type="primary" onClick={onSave}>
                            <Icon.Save className="me-2" size={17} />{t('save')}
                        </Button>,
                        <Button key="back" className="btn-gray" onClick={onCancel}>
                            {t('cancel')}
                        </Button>,
                    ]}>
                    <Form
                        layout="vertical"
                        name="add_edit_service_link"
                        preserve={false}
                        form={form}
                        initialValues={{status: true, enableNewTab: true, visibility: VISIBILITY_DEFAULT}}
                        validateMessages={{
                            required: t('required'),
                        }}>

                        {/*
                          * NMU-424: language-specific fields live in Thai/English tabs;
                          * everything below the tabs is shared configuration.
                          *
                          * forceRender keeps BOTH panes mounted. antd 4 leaves an
                          * unvisited pane unmounted, and this Form sets
                          * preserve={false} - so without it, editing a link and
                          * saving without ever opening the English tab would drop
                          * every English value that was loaded into the form.
                          */}
                        <Tabs activeKey={activeTab} onChange={setActiveTab}>
                            <TabPane tab="Thai" key={TAB_TH} forceRender>
                                {/* NMU-575: first field in the form. This is the
                                    couple of words printed under the icon on the
                                    card; ชื่อลิงค์บริการ below is the headline
                                    sentence beside it. */}
                                <Form.Item label={t('iconNameTH')} name="iconNameTH">
                                    <Input />
                                </Form.Item>

                                <Form.Item
                                    label={t('serviceLinkName')}
                                    name="title"
                                    rules={[
                                        {
                                            required: true,
                                        },
                                    ]}>
                                    <Input />
                                </Form.Item>

                                <Form.Item label={t('descriptionTH')} name="descriptionTH">
                                    <Input.TextArea rows={2} />
                                </Form.Item>

                                <Form.Item label={t('badgeTH')} name="badgeTH">
                                    <Input />
                                </Form.Item>
                            </TabPane>

                            <TabPane tab="English" key={TAB_EN} forceRender>
                                <Form.Item label={t('iconNameEN')} name="iconNameEN">
                                    <Input />
                                </Form.Item>

                                <Form.Item label="Service Link Name" name="titleEN">
                                    <Input />
                                </Form.Item>

                                <Form.Item label="Description (EN)" name="descriptionEN">
                                    <Input.TextArea rows={2} />
                                </Form.Item>

                                <Form.Item label="Tag / Label (EN)" name="badgeEN">
                                    <Input />
                                </Form.Item>
                            </TabPane>
                        </Tabs>

                        {/* NMU-413: image upload removed — the new design uses fixed icons. */}

                        {/* Everything below is shared by both languages. */}
                        <Divider orientation="left" plain className="mt-0">
                            {t('sharedSettings')}
                        </Divider>


                        {/*
                          NMU-448: the helper text under this dropdown is gone. The
                          icon still decides which Academic & Admissions slot a link
                          fills on the website (NMU-442) — that is just no longer
                          spelled out here, and the wording referred to a `category`
                          field NMU-431 removed and to the display order NMU-447 did.
                        */}
                        <Form.Item label={t('icon')} name="icon">
                            <Select allowClear placeholder={t('selectIcon')}>
                                {SERVICE_LINK_ICON_OPTIONS.map((opt) => (
                                    <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item label={t('linkType')} name="linkType" rules={[{ required: true }]}>
                            <Select placeholder={t('linkTypePlaceholder')}>
                                <Select.Option value={LINK_TYPE.CONTENT}>{t('linkTypeContent')}</Select.Option>
                                <Select.Option value={LINK_TYPE.URL}>{t('linkTypeUrl')}</Select.Option>
                            </Select>
                        </Form.Item>

                        {/*
                          NMU-470: only the field belonging to the chosen type is
                          shown, and only that one is required — requiring both
                          would make every link unsaveable.
                        */}
                        <Form.Item noStyle shouldUpdate={(prev, cur) => prev.linkType !== cur.linkType}>
                            {({ getFieldValue }) =>
                                getFieldValue('linkType') === LINK_TYPE.CONTENT ? (
                                    <Form.Item label={t('contentField')} name="templateId" rules={[{ required: true }]}>
                                        <ContentSelect placeholder={t('contentPlaceholder')} />
                                    </Form.Item>
                                ) : getFieldValue('linkType') === LINK_TYPE.URL ? (
                                    <Form.Item label={t('link')} name="url" rules={[{ required: true }]}>
                                        <Input />
                                    </Form.Item>
                                ) : null
                            }
                        </Form.Item>

                        <Form.Item label={t('keywords')} >
                            <EditableTagGroup onChange={setKeywordList} tags={keywordList} lang={lang} />
                        </Form.Item>

                        <Form.Item label={t('openInNewTab')} name="enableNewTab" valuePropName="checked">
                            <Switch />
                        </Form.Item>

                        <Form.Item label={t('status')} name="status" valuePropName="checked">
                            <Switch />
                        </Form.Item>

                        <VisibilityFormItem lang={lang} />

                    </Form>
                </Modal>
            </div>
        </div>
    )
}

export default AddEditServiceLinkDialog;